import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  IconButton,
  CircularProgress
} from "@mui/material";
import { AddPhotoAlternate, DeleteOutline } from "@mui/icons-material";
import { fleetOwnerApis } from "../../../Data/axios";

export default function EditOwner({ open, handleClose, ownerData, onOwnerUpdated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [state, setState] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [address, setAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [selectedRadio, setSelectedRadio] = useState("male");
  
  // File states
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [aadharFront, setAadharFront] = useState(null);
  const [aadharFrontUrl, setAadharFrontUrl] = useState("");
  const [aadharBack, setAadharBack] = useState(null);
  const [aadharBackUrl, setAadharBackUrl] = useState("");
  const [farmCertificate, setFarmCertificate] = useState(null);
  const [farmCertificateUrl, setFarmCertificateUrl] = useState("");
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with owner data when modal opens
  useEffect(() => {
    if (open && ownerData) {
      setName(ownerData.username || "");
      setEmail(ownerData.email || "");
      setMobile(ownerData.phone || "");
      setState(ownerData.state || "");
      setPinCode(ownerData.pincode || "");
      setAddress(ownerData.address || "");
      setCompanyName(ownerData.company_name || "");
      
      // Set gender based on numeric value
      if (ownerData.gender === 1) {
        setSelectedRadio("male");
      } else if (ownerData.gender === 2) {
        setSelectedRadio("female");
      } else {
        setSelectedRadio("others");
      }
      
      // Set image URLs if they exist
      setProfileImageUrl(ownerData.image || "");
      setAadharFrontUrl(ownerData.aadhar_front || "");
      setAadharBackUrl(ownerData.aadhar_back || "");
      setFarmCertificateUrl(ownerData.farm_certificate || "");
    }
  }, [open, ownerData]);

  const handleFormReset = () => {
    setName("");
    setEmail("");
    setMobile("");
    setState("");
    setPinCode("");
    setAddress("");
    setCompanyName("");
    setSelectedRadio("male");
    setProfileImage(null);
    setProfileImageUrl("");
    setAadharFront(null);
    setAadharFrontUrl("");
    setAadharBack(null);
    setAadharBackUrl("");
    setFarmCertificate(null);
    setFarmCertificateUrl("");
    setError(null);
  };

  const handleCloseModal = () => {
    handleFormReset();
    handleClose();
  };

  const handleFileChange = (event, setter) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setter(file);
    }
  };

  const handleFileRemove = (setter, urlSetter) => {
    setter(null);
    urlSetter("");
  };

  const handleUpdateOwner = async () => {
    try {
      // Form validation
      if (!name || !email || !mobile || !state || !pinCode || !address) {
        alert("Please fill in all required fields");
        return;
      }

      setLoading(true);
      setError(null);

      // Convert gender value
      let genderValue;
      if (selectedRadio === "male") {
        genderValue = 1;
      } else if (selectedRadio === "female") {
        genderValue = 2;
      } else {
        genderValue = 3; // Others
      }

      // Create FormData object
      const formData = new FormData();
      
      // Append text data
      formData.append("username", name);
      formData.append("email", email);
      formData.append("phone", mobile);
      formData.append("address", address);
      formData.append("state", state);
      formData.append("pincode", pinCode);
      formData.append("gender", genderValue);
      formData.append("company_name", companyName);

      // Append image files only if they have been newly selected
      if (profileImage) {
        formData.append("image", profileImage);
      }
      
      if (aadharFront) {
        formData.append("aadhar_front", aadharFront);
      }
      
      if (aadharBack) {
        formData.append("aadhar_back", aadharBack);
      }
      
      if (farmCertificate) {
        formData.append("farm_certificate", farmCertificate);
      }

      // Send data to API
      const response = await fleetOwnerApis.updatebyId(ownerData.user_id, formData);
      
      // Handle success
      if (response && response.success) {
        alert("Fleet owner updated successfully!");
        if (onOwnerUpdated) onOwnerUpdated();
        handleCloseModal();
      } else {
        // More detailed error handling
        const errorMessage = response?.message || "Failed to update fleet owner. Please try again.";
        console.error("API Error:", response);
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error updating fleet owner:", error);
      
      // More descriptive error message
      let errorMessage = "An error occurred while updating the fleet owner.";
      if (error.response) {
        console.error("Error response:", error.response.data);
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        console.error("Error request:", error.request);
        errorMessage = "No response received from server. Please check your connection.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiInputBase-input": {
        color: "#424242",
      },
    },
  };

  // Component for file inputs with image preview
  const FileInput = ({ title, file, fileUrl, onFileChange, onFileRemove, accept }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
        {title}
      </Typography>
      <Box
        sx={{
          border: "1px dashed #bdbdbd",
          borderRadius: 1,
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          position: "relative",
          minHeight: "120px",
        }}
      >
        {file ? (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", fontFamily: 'Ubuntu, sans-serif' }}>
                {file.name}
              </Typography>
              <IconButton onClick={() => onFileRemove()} size="small" color="error">
                <DeleteOutline />
              </IconButton>
            </Box>
            {file.type.startsWith("image/") && (
              <Box 
                sx={{ 
                  width: "100%", 
                  height: 100, 
                  backgroundImage: `url(${URL.createObjectURL(file)})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            )}
          </Box>
        ) : fileUrl ? (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", fontFamily: 'Ubuntu, sans-serif' }}>
                Current {title}
              </Typography>
              <IconButton onClick={() => onFileRemove()} size="small" color="error">
                <DeleteOutline />
              </IconButton>
            </Box>
            <Box 
              sx={{ 
                width: "100%", 
                height: 100, 
                backgroundImage: `url(${fileUrl})`,
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
          </Box>
        ) : (
          <>
            <input
              type="file"
              id={`file-input-${title.replace(/\s+/g, '-').toLowerCase()}`}
              style={{ display: "none" }}
              onChange={(e) => onFileChange(e)}
              accept={accept || "image/*"}
            />
            <label htmlFor={`file-input-${title.replace(/\s+/g, '-').toLowerCase()}`}>
              <Button
                variant="text"
                component="span"
                startIcon={<AddPhotoAlternate />}
                sx={{ color: "#1d82f5", fontFamily: 'Ubuntu, sans-serif' }}
              >
                Upload File
              </Button>
            </label>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, fontFamily: 'Ubuntu, sans-serif' }}>
              Click to upload or drag and drop
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Modal open={open} onClose={handleCloseModal} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
            fontFamily: 'Ubuntu, sans-serif'
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
              Edit Fleet Owner
            </Typography>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
                fontFamily: 'Ubuntu, sans-serif'
              }}
            >
              Close
            </Button>
          </Box>

          {error && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "#fdeded", borderRadius: 1, color: "#d32f2f" }}>
              <Typography variant="body2" sx={{ fontFamily: 'Ubuntu, sans-serif' }}>{error}</Typography>
            </Box>
          )}

          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  {/* Personal Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: "#212121", mb: 2, fontFamily: 'Ubuntu, sans-serif' }}>
                      Personal Information
                    </Typography>
                  </Grid>

                  {/* Profile Image */}
                  <Grid item xs={12} md={3}>
                    <FileInput
                      title="Profile Image"
                      file={profileImage}
                      fileUrl={profileImageUrl}
                      onFileChange={(e) => handleFileChange(e, setProfileImage)}
                      onFileRemove={() => handleFileRemove(setProfileImage, setProfileImageUrl)}
                      accept="image/*"
                    />
                  </Grid>
                
                  {/* Name */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter name"
                      size="small"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="example@email.com"
                      size="small"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Phone <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="+91 1234567890"
                      size="small"
                      value={mobile}
                      disabled
                      onChange={(e) => setMobile(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Gender <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl>
                      <RadioGroup
                        row
                        value={selectedRadio}
                        onChange={(e) => setSelectedRadio(e.target.value)}
                      >
                        <FormControlLabel
                          value="male"
                          control={
                            <Radio
                              sx={{
                                color: "#9e9e9e",
                                "&.Mui-checked": { color: "#1d82f5" },
                              }}
                            />
                          }
                          label="Male"
                          sx={{ color: "#424242", fontFamily: 'Ubuntu, sans-serif' }}
                        />
                        <FormControlLabel
                          value="female"
                          control={
                            <Radio
                              sx={{
                                color: "#9e9e9e",
                                "&.Mui-checked": { color: "#1d82f5" },
                              }}
                            />
                          }
                          label="Female"
                          sx={{ color: "#424242", fontFamily: 'Ubuntu, sans-serif' }}
                        />
                        <FormControlLabel
                          value="others"
                          control={
                            <Radio
                              sx={{
                                color: "#9e9e9e",
                                "&.Mui-checked": { color: "#1d82f5" },
                              }}
                            />
                          }
                          label="Others"
                          sx={{ color: "#424242", fontFamily: 'Ubuntu, sans-serif' }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Company Name */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Company Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter company name"
                      size="small"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* State */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      State <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter state"
                      size="small"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Pin Code */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Pin Code <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter pin code"
                      size="small"
                      value={pinCode}
                      onChange={(e) => setPinCode(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121", fontFamily: 'Ubuntu, sans-serif' }}>
                      Address <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter full address"
                      size="small"
                      multiline
                      rows={2}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Document Section */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ color: "#212121", mb: 2, fontFamily: 'Ubuntu, sans-serif' }}>
                      Documents
                    </Typography>
                  </Grid>

                  {/* Aadhar Front */}
                  <Grid item xs={12} md={4}>
                    <FileInput
                      title="Aadhar Card (Front)"
                      file={aadharFront}
                      fileUrl={aadharFrontUrl}
                      onFileChange={(e) => handleFileChange(e, setAadharFront)}
                      onFileRemove={() => handleFileRemove(setAadharFront, setAadharFrontUrl)}
                    />
                  </Grid>

                  {/* Aadhar Back */}
                  <Grid item xs={12} md={4}>
                    <FileInput
                      title="Aadhar Card (Back)"
                      file={aadharBack}
                      fileUrl={aadharBackUrl}
                      onFileChange={(e) => handleFileChange(e, setAadharBack)}
                      onFileRemove={() => handleFileRemove(setAadharBack, setAadharBackUrl)}
                    />
                  </Grid>

                  {/* FARM Certificate */}
                  <Grid item xs={12} md={4}>
                    <FileInput
                      title="FARM Certificate"
                      file={farmCertificate}
                      fileUrl={farmCertificateUrl}
                      onFileChange={(e) => handleFileChange(e, setFarmCertificate)}
                      onFileRemove={() => handleFileRemove(setFarmCertificate, setFarmCertificateUrl)}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Form buttons */}
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={handleUpdateOwner}
                  disabled={loading}
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    fontWeight: 500,
                    textTransform: "none",
                    padding: "8px 16px",
                    fontFamily: 'Ubuntu, sans-serif'
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Update Fleet Owner"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  disabled={loading}
                  sx={{
                    borderColor: "#bdbdbd",
                    color: "#424242",
                    "&:hover": {
                      borderColor: "#9e9e9e",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                    padding: "8px 16px",
                    fontFamily: 'Ubuntu, sans-serif'
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
}